<script setup lang="ts">
import { computed } from 'vue';
import { marked } from 'marked';
import type { InvestigationPath, InvestigationTargetRef } from '@/ai/utils/types';
import { useChatStore } from '@/ai/store/chat';
import { useInternational } from '@/common/locale';

const { t } = useInternational();

const props = defineProps<{
  path: InvestigationPath;
}>();

const emit = defineEmits<{
  /** 연계 분석 액션 클릭 — suggestion pill 과 동일하게 입력창 채움 + 즉시 전송 흐름을 탄다. */
  (e: 'action-click', message: string): void;
}>();

const chatStore = useChatStore();

/** 배너의 인라인 마크다운(`code` 강조)만 HTML 로 변환 — 블록 요소 없이 한 문장 유지. */
const bannerHtml = computed(() => marked.parseInline(props.path.banner ?? '') as string);

const related = computed(() => props.path.related ?? []);
const actions = computed(() =>
  (props.path.actions ?? []).filter((a) => !!a?.label && !!a?.message),
);

/** targetKind → 노드 아이콘 (이관 설계의 ⚙️/🗄 표기 유지, 미정의 kind 는 📦). */
const KIND_EMOJI: Record<string, string> = {
  application: '⚙️',
  was: '⚙️',
  webserver: '🌐',
  web_server: '🌐',
  database: '🗄',
  host: '🖥️',
  container_host: '🖥️',
  node: '🖥️',
  pod: '📦',
  container: '📦',
};
const kindEmoji = (kind: string) => KIND_EMOJI[(kind ?? '').toLowerCase()] ?? '📦';

/** 노드 보조 라벨 — subKind 가 없으면 계층명만. */
const nodeSub = (t: InvestigationTargetRef, layer: string) =>
  t.subKind ? `${t.subKind} · ${layer}` : layer;

/**
 * BotMessage 본문의 target:// 링크와 동일한 36-char UUID 검증 — invalid id (truncated
 * prefix / placeholder) 를 backend 로 보내 5xx 가 나는 것을 차단. 실패 시 플레인 텍스트.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isClickable = (t: InvestigationTargetRef) => UUID_RE.test(t.targetId ?? '');

/** 타겟명 클릭 — 본문 target:// 링크와 동일한 상세 슬라이드 오픈 흐름 재사용. */
const onTargetClick = (t: InvestigationTargetRef) => {
  if (!isClickable(t)) return;
  chatStore.dispatchTargetDetailFromBot({
    type: t.targetKind,
    target_id: t.targetId,
    target_name: t.name,
    sub_kind: t.subKind,
  });
};

const onActionClick = (message: string) => {
  emit('action-click', message);
};
</script>

<template>
  <div
    class="investigation-path"
    data-testid="bot-investigation-path"
  >
    <!-- ⛓️ 확장 조사 배너 -->
    <div class="investigation-path__banner">
      <span
        class="investigation-path__banner-icon"
        aria-hidden="true"
        >⛓️</span
      >
      <span
        v-dompurify-html="bannerHtml"
        class="investigation-path__banner-text"
      />
    </div>

    <!-- 🧭 추적 경로 체인: source ➜ related (여러 개면 세로 나열) -->
    <div class="investigation-path__chain">
      <span
        class="investigation-path__chain-icon"
        aria-hidden="true"
        >🧭</span
      >
      <div class="investigation-path__node investigation-path__node--source">
        <button
          v-if="isClickable(path.source)"
          type="button"
          class="investigation-path__target"
          @click="() => onTargetClick(path.source)"
        >
          {{ kindEmoji(path.source.targetKind) }} {{ path.source.name }}
        </button>
        <span
          v-else
          class="investigation-path__target investigation-path__target--plain"
        >
          {{ kindEmoji(path.source.targetKind) }} {{ path.source.name }}
        </span>
        <span class="investigation-path__node-sub">{{
          nodeSub(path.source, t('WORD.CHATBOT.MESSAGE.SYMPTOM_LAYER'))
        }}</span>
      </div>
      <div class="investigation-path__related">
        <div
          v-for="(r, idx) in related"
          :key="`inv-rel-${idx}`"
          class="investigation-path__hop"
        >
          <span
            class="investigation-path__arrow"
            aria-hidden="true"
            >➜</span
          >
          <div class="investigation-path__node investigation-path__node--related">
            <button
              v-if="isClickable(r)"
              type="button"
              class="investigation-path__target"
              @click="() => onTargetClick(r)"
            >
              {{ kindEmoji(r.targetKind) }} {{ r.name }}
            </button>
            <span
              v-else
              class="investigation-path__target investigation-path__target--plain"
            >
              {{ kindEmoji(r.targetKind) }} {{ r.name }}
            </span>
            <span class="investigation-path__node-sub">{{
              nodeSub(r, t('WORD.CHATBOT.MESSAGE.ROOT_CAUSE_LAYER'))
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 연계 분석 액션 버튼 -->
    <div
      v-if="actions.length > 0"
      class="investigation-path__actions"
    >
      <button
        v-for="(a, idx) in actions"
        :key="`inv-act-${idx}`"
        type="button"
        class="investigation-path__action"
        @click="() => onActionClick(a.message)"
      >
        {{ a.label }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.investigation-path {
  /* StructuredAnswer critical 변형과 동일한 위험(붉은) 계열 토큰 조합 */
  --ip-accent: var(--connection-test-fail-color, #d32f2f);
  --ip-tint: var(--feedback-error-bg-color, #fdecea);

  display: flex;
  flex-direction: column;
  margin-top: 8px;
  overflow: hidden;
  border: 1px solid var(--color-gray-02-06, #e0e3e8);
  border-left: 3px solid var(--ip-accent);
  border-radius: 8px;
  background-color: var(--config-group-box-bg-color, #ffffff);
  font-size: 13px; /* 챗봇 본문(general-message__content 13px)과 일치 */

  /* ── 배너 ── */
  &__banner {
    display: flex;
    gap: 7px;
    align-items: flex-start;
    padding: 10px 12px;
    border-bottom: 1px solid var(--color-gray-01-04, #eceef1);
    background-color: var(--ip-tint);
    font-weight: 600;
    line-height: 1.5;
    color: var(--main-font-color, #1f2329);
  }

  &__banner-icon {
    flex-shrink: 0;
  }

  &__banner-text {
    flex: 1;
    overflow-wrap: anywhere;

    :deep(code) {
      padding: 1px 5px;
      border-radius: 4px;
      background-color: var(--config-group-box-bg-color, #ffffff);
      font-family: var(--font-family-code);
      font-size: 12px;
      font-weight: 700;
      color: var(--ip-accent);
    }
  }

  /* ── 추적 경로 체인 ── */
  &__chain {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 10px 12px;
  }

  &__chain-icon {
    flex-shrink: 0;
  }

  &__related {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__hop {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__arrow {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ip-accent);
  }

  &__node {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding: 6px 10px;
    border: 1px solid var(--color-gray-02-06, #e0e3e8);
    border-radius: 6px;
    background-color: var(--color-gray-09-00);
  }

  &__node--related {
    border-color: var(--ip-accent);
    background-color: var(--ip-tint);
  }

  &__target {
    padding: 0;
    margin: 0;
    cursor: pointer;
    border: none;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    color: var(--main-font-color, #1f2329);
    text-align: left;
    overflow-wrap: anywhere;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  &__target--plain {
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }

  &__node-sub {
    font-size: 11px;
    color: var(--sub-font-color, #545b65);
  }

  /* ── 연계 분석 액션 ── */
  &__actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    padding: 10px 12px;
    border-top: 1px solid var(--color-gray-01-04, #eceef1);
  }

  &__action {
    display: block;
    max-width: 100%;
    padding: 7px 12px;
    cursor: pointer;
    border: 1px solid var(--ip-accent);
    border-radius: 16px;
    background: var(--color-gray-09-00);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--ip-accent);
    text-align: left;
    white-space: normal;
    transition:
      background 0.15s ease,
      color 0.15s ease;
    overflow-wrap: anywhere;

    &:hover {
      background: var(--ip-tint);
    }

    &:active {
      transform: translateY(1px);
    }
  }
}
</style>
